import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ExternalLink, Plus, CheckCircle, Music, Search, Shuffle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSpotify } from './SpotifyContext';
import { SortableSongList } from './components/SortableSongList';
import { API_URL } from './config';

const API = API_URL;

// Géneros musicales
const GENRES = [
  { value: '', label: 'Todos los géneros' },
  { value: 'Reggaeton', label: '🎵 Reggaeton' },
  { value: 'Pop', label: '🎤 Pop' },
  { value: 'Pop español', label: '🇪🇸 Pop Español' },
  { value: 'Bachata', label: '💃 Bachata' },
  { value: 'Salsa', label: '🎺 Salsa' },
  { value: 'Baladas', label: '💕 Baladas' },
  { value: 'Dance', label: '🕺 Dance / Electrónica' },
  { value: 'Rock', label: '🎸 Rock' },
  { value: 'Hip-Hop', label: '🎤 Hip-Hop / Rap' },
  { value: 'Latino', label: '🌴 Latino' },
  { value: 'Rumbas', label: '🔥 Rumbas' },
  { value: 'Indie', label: '🎧 Indie' },
  { value: 'Verano', label: '☀️ Canción del Verano' },
  { value: 'Clásica', label: '🎻 Clásica / Instrumental' },
];

// Décadas
const DECADES = [
  { value: '', label: 'Cualquier época' },
  { value: '2020s', label: '📅 Actual (2020s)' },
  { value: '2010s', label: '📅 Años 2010' },
  { value: '2000s', label: '📅 Años 2000' },
  { value: '1990s', label: '📅 Años 90' },
  { value: '1980s', label: '📅 Años 80' },
  { value: '1970s', label: '📅 Años 70' },
  { value: 'clasicos', label: '🏆 Clásicos de siempre' },
];

// Tipos de evento
const EVENT_TYPES = [
  { value: '', label: 'Cualquier evento' },
  { value: 'boda', label: '💒 Boda (fiesta)' },
  { value: 'comunion', label: '⛪ Comunión' },
  { value: 'cumpleaños', label: '🎂 Cumpleaños' },
  { value: 'corporativo', label: '🏢 Evento corporativo' },
  { value: 'fiesta', label: '🎉 Fiesta general' },
  { value: 'verbena', label: '🎪 Verbena / Feria' },
];

// Momentos del evento
const EVENT_MOMENTS = [
  { value: '', label: 'Cualquier momento' },
  // Momentos de ceremonia/banquete (más tranquilos)
  { value: 'ceremonia', label: '⛪ Ceremonia' },
  { value: 'entrada_banquete', label: '🚪 Entrada a banquete' },
  { value: 'primer_baile', label: '💑 Primer baile novios' },
  { value: 'baile_padre', label: '👨‍👧 Baile padre-hija' },
  { value: 'baile_madre', label: '👩‍👦 Baile madre-hijo' },
  { value: 'corte_tarta', label: '🎂 Corte de tarta' },
  { value: 'brindis', label: '🥂 Brindis' },
  // Momentos de fiesta (bailables)
  { value: 'apertura_pista', label: '🎵 Apertura de pista' },
  { value: 'hora_loca', label: '🤪 Hora loca' },
  { value: 'fiesta', label: '🕺 Fiesta / Baile' },
  { value: 'cierre', label: '🌙 Cierre / Última canción' },
];

// Idiomas
const LANGUAGES = [
  { value: '', label: 'Cualquier idioma' },
  { value: 'español', label: '🇪🇸 Español' },
  { value: 'ingles', label: '🇬🇧 Inglés' },
  { value: 'italiano', label: '🇮🇹 Italiano' },
  { value: 'frances', label: '🇫🇷 Francés' },
  { value: 'instrumental', label: '🎼 Instrumental' },
];

export const TendenciasView = () => {
  const { isConnected: spotifyConnected, accessToken } = useSpotify();
  
  // Filtros de búsqueda
  const [genreFilter, setGenreFilter] = useState('');
  const [decadeFilter, setDecadeFilter] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [momentFilter, setMomentFilter] = useState('');
  const [languageFilter, setLanguageFilter] = useState('');
  const [artistFilter, setArtistFilter] = useState('');
  
  // Estados de resultados
  const [showOnlyNotInLibrary, setShowOnlyNotInLibrary] = useState(false);
  const [songs, setSongs] = useState([]);
  const [filteredSongs, setFilteredSongs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSongs, setSelectedSongs] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [lastSearchParams, setLastSearchParams] = useState('');
  
  // Spotify Queue states
  const [selectedForSpotify, setSelectedForSpotify] = useState([]);
  const [isSpotifyDialogOpen, setIsSpotifyDialogOpen] = useState(false);
  const [spotifyOptions, setSpotifyOptions] = useState({ shuffle: false, repeat: 'off' });

  useEffect(() => {
    // Don't fetch automatically on mount, wait for user to click search
  }, []);

  useEffect(() => {
    applyFilters();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps: applyFilters
  }, [songs, showOnlyNotInLibrary, searchQuery]);

  const fetchTrendingSongs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: 50
      });
      
      if (genreFilter) params.append('genre', genreFilter);
      if (decadeFilter) params.append('decade', decadeFilter);
      if (eventTypeFilter) params.append('event_type', eventTypeFilter);
      if (momentFilter) params.append('moment', momentFilter);
      if (languageFilter) params.append('language', languageFilter);
      if (artistFilter) params.append('artist', artistFilter);
      
      // Guardar parámetros de búsqueda para mostrar al usuario
      const searchDesc = [
        genreFilter && `Género: ${genreFilter}`,
        decadeFilter && `Época: ${DECADES.find(d => d.value === decadeFilter)?.label}`,
        eventTypeFilter && `Evento: ${EVENT_TYPES.find(e => e.value === eventTypeFilter)?.label}`,
        momentFilter && `Momento: ${EVENT_MOMENTS.find(m => m.value === momentFilter)?.label}`,
        languageFilter && `Idioma: ${LANGUAGES.find(l => l.value === languageFilter)?.label}`,
        artistFilter && `Artista: ${artistFilter}`,
      ].filter(Boolean).join(' • ');
      setLastSearchParams(searchDesc || 'Búsqueda general');
      
      const response = await axios.get(`${API}/search/songs?${params}`);
      setSongs(response.data.results || []);
      setSelectedSongs(new Set());
      
      if (response.data.results?.length === 0) {
        toast.info('No se encontraron resultados. Prueba con otros filtros.');
      } else {
        toast.success(`${response.data.results.length} canciones encontradas`);
      }
    } catch (error) {
      console.error('Error fetching songs:', error);
      toast.error('Error al buscar canciones');
    }
    setLoading(false);
  };

  const applyFilters = () => {
    let filtered = [...songs];

    // Filter by "not in library"
    if (showOnlyNotInLibrary) {
      filtered = filtered.filter(song => !song.in_library);
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(song => 
        song.title.toLowerCase().includes(query) ||
        song.artist.toLowerCase().includes(query)
      );
    }

    setFilteredSongs(filtered);
  };

  const toggleSongSelection = (song) => {
    const newSelected = new Set(selectedSongs);
    const key = `${song.title}-${song.artist}`;
    
    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    
    setSelectedSongs(newSelected);
  };

  const toggleSpotifySelection = (song) => {
    const isSelected = selectedForSpotify.some(s => s.spotify_uri === song.spotify_uri);
    if (isSelected) {
      setSelectedForSpotify(selectedForSpotify.filter(s => s.spotify_uri !== song.spotify_uri));
    } else {
      setSelectedForSpotify([...selectedForSpotify, song]);
    }
  };

  const reorderSpotifySelection = (newOrder) => {
    setSelectedForSpotify(newOrder);
  };

  const removeFromSpotifySelection = (songUri) => {
    setSelectedForSpotify(selectedForSpotify.filter(s => s.spotify_uri !== songUri));
  };

  const sendToSpotify = async () => {
    if (selectedForSpotify.length === 0) {
      toast.error('Selecciona al menos una canción');
      return;
    }

    try {
      const track_uris = selectedForSpotify.map(song => song.spotify_uri);

      await axios.post(`${API}/spotify/queue/add-multiple`, {
        access_token: accessToken,
        track_uris,
      });

      // Shuffle y repeat son controles independientes en la Spotify Web API:
      // si el usuario los activó en el diálogo, los aplicamos tras encolar.
      if (spotifyOptions.shuffle) {
        try {
          await axios.put(`${API}/spotify/playback/shuffle`, {
            access_token: accessToken,
            state: true,
          });
        } catch (e) { /* best-effort */ }
      }
      if (spotifyOptions.repeat && spotifyOptions.repeat !== 'off') {
        try {
          await axios.put(`${API}/spotify/playback/repeat`, {
            access_token: accessToken,
            state: spotifyOptions.repeat,
          });
        } catch (e) { /* best-effort */ }
      }

      toast.success(`${selectedForSpotify.length} canciones añadidas a Spotify`);
      setIsSpotifyDialogOpen(false);
      setSelectedForSpotify([]);
      setSpotifyOptions({ shuffle: false, repeat: 'off' });
    } catch (error) {
      console.error('Error sending to Spotify:', error);
      toast.error('Error al enviar a Spotify');
    }
  };

  const addSingleSong = async (song) => {
    try {
      const response = await axios.post(`${API}/trending/add-multiple`, {
        songs_data: [song]
      });
      
      if (response.data.added > 0) {
        toast.success(`"${song.title}" añadida a biblioteca`);
        // Refresh to update in_library status
        fetchTrendingSongs();
      } else if (response.data.skipped > 0) {
        toast.info('Esta canción ya está en tu biblioteca');
      }
    } catch (error) {
      console.error('Error adding song:', error);
      toast.error('Error al añadir canción');
    }
  };

  const addSelectedSongs = async () => {
    if (selectedSongs.size === 0) {
      toast.error('Selecciona al menos una canción');
      return;
    }

    try {
      const songsToAdd = filteredSongs.filter(song => 
        selectedSongs.has(`${song.title}-${song.artist}`)
      );

      const response = await axios.post(`${API}/trending/add-multiple`, {
        songs_data: songsToAdd
      });

      toast.success(`${response.data.added} canciones añadidas, ${response.data.skipped} ya existían`);
      setSelectedSongs(new Set());
      fetchTrendingSongs();
    } catch (error) {
      console.error('Error adding songs:', error);
      toast.error('Error al añadir canciones');
    }
  };

  const getEnergyBadge = (energia) => {
    if (!energia) return null;
    
    const config = {
      'Muy Alta': { emoji: '⚡⚡⚡⚡', color: 'bg-red-600/30 text-red-300' },
      'Alta': { emoji: '⚡⚡⚡', color: 'bg-orange-600/30 text-orange-300' },
      'Media': { emoji: '⚡⚡', color: 'bg-yellow-600/30 text-yellow-300' },
      'Baja': { emoji: '⚡', color: 'bg-blue-600/30 text-blue-300' }
    };

    const conf = config[energia] || config['Media'];
    return (
      <span className={`inline-block px-2 py-1 rounded text-xs ${conf.color}`}>
        {conf.emoji} {energia}
      </span>
    );
  };

  return (
    <div className="max-w-7xl mx-auto p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">🔍 Buscador de Canciones</h1>
          <p className="text-slate-400 text-sm">Encuentra la canción perfecta para cada momento</p>
        </div>
      </div>

      {/* Filters Section */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {/* Genre Filter */}
            <div>
              <Label className="text-slate-300 text-xs mb-1">Género</Label>
              <select
                className="w-full bg-slate-900 border-slate-700 text-white rounded px-3 py-2 text-sm"
                value={genreFilter}
                onChange={(e) => setGenreFilter(e.target.value)}
              >
                {GENRES.map(genre => (
                  <option key={genre.value} value={genre.value}>{genre.label}</option>
                ))}
              </select>
            </div>

            {/* Decade Filter */}
            <div>
              <Label className="text-slate-300 text-xs mb-1">Época</Label>
              <select
                className="w-full bg-slate-900 border-slate-700 text-white rounded px-3 py-2 text-sm"
                value={decadeFilter}
                onChange={(e) => setDecadeFilter(e.target.value)}
              >
                {DECADES.map(decade => (
                  <option key={decade.value} value={decade.value}>{decade.label}</option>
                ))}
              </select>
            </div>

            {/* Event Type Filter */}
            <div>
              <Label className="text-slate-300 text-xs mb-1">Tipo de evento</Label>
              <select
                className="w-full bg-slate-900 border-slate-700 text-white rounded px-3 py-2 text-sm"
                value={eventTypeFilter}
                onChange={(e) => setEventTypeFilter(e.target.value)}
              >
                {EVENT_TYPES.map(event => (
                  <option key={event.value} value={event.value}>{event.label}</option>
                ))}
              </select>
            </div>

            {/* Moment Filter */}
            <div>
              <Label className="text-slate-300 text-xs mb-1">Momento</Label>
              <select
                className="w-full bg-slate-900 border-slate-700 text-white rounded px-3 py-2 text-sm"
                value={momentFilter}
                onChange={(e) => setMomentFilter(e.target.value)}
              >
                {EVENT_MOMENTS.map(moment => (
                  <option key={moment.value} value={moment.value}>{moment.label}</option>
                ))}
              </select>
            </div>

            {/* Language Filter */}
            <div>
              <Label className="text-slate-300 text-xs mb-1">Idioma</Label>
              <select
                className="w-full bg-slate-900 border-slate-700 text-white rounded px-3 py-2 text-sm"
                value={languageFilter}
                onChange={(e) => setLanguageFilter(e.target.value)}
              >
                {LANGUAGES.map(lang => (
                  <option key={lang.value} value={lang.value}>{lang.label}</option>
                ))}
              </select>
            </div>

            {/* Artist Search */}
            <div>
              <Label className="text-slate-300 text-xs mb-1">Artista</Label>
              <Input
                className="bg-slate-900 border-slate-700 text-white text-sm h-[38px]"
                placeholder="Ej: Rosalía"
                value={artistFilter}
                onChange={(e) => setArtistFilter(e.target.value)}
              />
            </div>
          </div>

          {/* Search Button and Options */}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button
              onClick={fetchTrendingSongs}
              disabled={loading}
              className="bg-cyan-600 hover:bg-cyan-500"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Buscando...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  Buscar Canciones
                </>
              )}
            </Button>

            <Button
              variant="outline"
              onClick={() => {
                setGenreFilter('');
                setDecadeFilter('');
                setEventTypeFilter('');
                setMomentFilter('');
                setLanguageFilter('');
                setArtistFilter('');
                setSongs([]);
                setLastSearchParams('');
              }}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              Limpiar filtros
            </Button>

            <div className="flex items-center gap-2 ml-auto">
              <input
                type="checkbox"
                id="notInLibrary"
                checked={showOnlyNotInLibrary}
                onChange={(e) => setShowOnlyNotInLibrary(e.target.checked)}
                className="rounded border-slate-600"
              />
              <label htmlFor="notInLibrary" className="text-slate-300 text-sm cursor-pointer">
                Solo las que no tengo
              </label>
            </div>
          </div>

          {/* Last search params */}
          {lastSearchParams && (
            <div className="mt-3 text-xs text-slate-400">
              <span className="text-slate-500">Búsqueda:</span> {lastSearchParams}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Filter in results */}
      {songs.length > 0 && (
        <div className="flex items-center gap-3">
          <Input
            className="bg-slate-800 border-slate-700 text-white text-sm max-w-xs"
            placeholder="Filtrar resultados..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <span className="text-slate-400 text-sm">
            {filteredSongs.length} de {songs.length} resultados
          </span>
          {selectedSongs.size > 0 && (
            <Button
              onClick={addSelectedSongs}
              className="bg-cyan-600 hover:bg-cyan-700 text-white ml-auto"
              size="sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Añadir {selectedSongs.size} seleccionadas
            </Button>
          )}
        </div>
      )}

      {/* Results */}
      <div className="text-slate-400 text-sm">
        {loading ? 'Buscando...' : songs.length > 0 ? `${filteredSongs.length} canciones` : 'Usa los filtros y pulsa "Buscar Canciones"'}
      </div>

      {/* Songs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredSongs.map((song, index) => {
          const isSelected = selectedSongs.has(`${song.title}-${song.artist}`);
          
          return (
            <Card 
              key={index} 
              className={`bg-slate-800 border-slate-700 hover:border-slate-600 transition-all ${
                isSelected ? 'border-cyan-500 ring-2 ring-cyan-500/50' : ''
              }`}
            >
              <CardContent className="p-3">
                <div className="flex gap-3">
                  {/* Checkboxes Column */}
                  <div className="flex flex-col gap-2 items-start pt-1">
                    {/* Checkbox for library */}
                    {!song.in_library && (
                      <input
                        type="checkbox"
                        className="w-4 h-4 cursor-pointer"
                        checked={isSelected}
                        onChange={() => toggleSongSelection(song)}
                        title="Añadir a biblioteca"
                      />
                    )}
                    
                    {/* Checkbox for Spotify Queue */}
                    {spotifyConnected && song.spotify_uri && (
                      <input
                        type="checkbox"
                        className="w-4 h-4 cursor-pointer accent-green-600"
                        checked={selectedForSpotify.some(s => s.spotify_uri === song.spotify_uri)}
                        onChange={() => toggleSpotifySelection(song)}
                        title="Enviar a Spotify"
                      />
                    )}
                  </div>

                  {/* Album Art */}
                  <div className="flex-shrink-0">
                    {song.image_url ? (
                      <img 
                        src={song.image_url} 
                        alt={song.title}
                        className="w-16 h-16 rounded object-cover"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-slate-700 rounded flex items-center justify-center">
                        <Music className="w-8 h-8 text-slate-500" />
                      </div>
                    )}
                  </div>

                  {/* Song Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-semibold text-sm line-clamp-1">
                      {song.title}
                    </h3>
                    <p className="text-slate-400 text-xs mb-2">{song.artist}</p>

                    {/* Badges */}
                    <div className="flex flex-wrap gap-1 mb-2">
                      {song.bpm && (
                        <span className="inline-block px-2 py-0.5 bg-slate-700 text-slate-300 rounded text-xs">
                          {song.bpm} BPM
                        </span>
                      )}
                      {getEnergyBadge(song.energia)}
                      {song.popularity && (
                        <span className="inline-block px-2 py-0.5 bg-amber-600/30 text-amber-300 rounded text-xs">
                          ⭐ {song.popularity}%
                        </span>
                      )}
                    </div>

                    {/* Genres */}
                    {song.genres && song.genres.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {song.genres.slice(0, 2).map((genre, i) => (
                          <span key={i} className="inline-block px-2 py-0.5 bg-purple-600/30 text-purple-300 rounded text-xs">
                            {genre}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 mt-2">
                      <a
                        href={song.spotify_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs"
                      >
                        <Button size="sm" variant="outline" className="h-7 text-xs bg-green-600/20 hover:bg-green-600/40 border-green-600">
                          <ExternalLink className="w-3 h-3 mr-1" />
                          Spotify
                        </Button>
                      </a>

                      {song.in_library ? (
                        <Button size="sm" variant="outline" className="h-7 text-xs bg-slate-700 border-slate-600" disabled>
                          <CheckCircle className="w-3 h-3 mr-1" />
                          En biblioteca
                        </Button>
                      ) : (
                        <Button 
                          size="sm" 
                          className="h-7 text-xs bg-cyan-600 hover:bg-cyan-700"
                          onClick={() => addSingleSong(song)}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Añadir
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredSongs.length === 0 && !loading && (
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-8 text-center">
            <p className="text-slate-400">No se encontraron canciones con los filtros aplicados</p>
          </CardContent>
        </Card>
      )}

      {/* Floating Spotify Button */}
      {spotifyConnected && selectedForSpotify.length > 0 && (
        <div className="fixed bottom-6 right-6 z-50">
          <Button
            onClick={() => setIsSpotifyDialogOpen(true)}
            className="bg-green-600 hover:bg-green-700 text-white shadow-2xl px-6 py-6 text-lg font-semibold"
            size="lg"
          >
            <Music className="w-6 h-6 mr-2" />
            Enviar {selectedForSpotify.length} a Spotify
          </Button>
        </div>
      )}

      {/* Spotify Send Dialog */}
      <Dialog open={isSpotifyDialogOpen} onOpenChange={setIsSpotifyDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">Enviar a Cola de Spotify</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Sortable List */}
            <SortableSongList
              songs={selectedForSpotify}
              onReorder={reorderSpotifySelection}
              onRemove={removeFromSpotifySelection}
            />

            {/* Options */}
            <div className="space-y-3 pt-4 border-t border-slate-700">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="spotify-shuffle-tendencias"
                  checked={spotifyOptions.shuffle}
                  onChange={(e) => setSpotifyOptions(prev => ({ ...prev, shuffle: e.target.checked }))}
                  className="w-5 h-5 rounded border-slate-600 bg-slate-700 text-green-600 focus:ring-green-500"
                />
                <label htmlFor="spotify-shuffle-tendencias" className="text-white cursor-pointer flex items-center gap-2">
                  <Shuffle className="w-5 h-5 text-green-400" />
                  Activar Mezcla (Shuffle)
                </label>
              </div>

              <div className="space-y-2">
                <label className="text-white text-sm font-medium">Modo Repetición:</label>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setSpotifyOptions(prev => ({ ...prev, repeat: 'off' }))}
                    variant="outline"
                    size="sm"
                    className={`${
                      spotifyOptions.repeat === 'off'
                        ? 'bg-green-600 border-green-600 text-white'
                        : 'bg-slate-700 border-slate-600 text-white'
                    }`}
                  >
                    Off
                  </Button>
                  <Button
                    onClick={() => setSpotifyOptions(prev => ({ ...prev, repeat: 'context' }))}
                    variant="outline"
                    size="sm"
                    className={`${
                      spotifyOptions.repeat === 'context'
                        ? 'bg-green-600 border-green-600 text-white'
                        : 'bg-slate-700 border-slate-600 text-white'
                    }`}
                  >
                    Repeat All
                  </Button>
                  <Button
                    onClick={() => setSpotifyOptions(prev => ({ ...prev, repeat: 'track' }))}
                    variant="outline"
                    size="sm"
                    className={`${
                      spotifyOptions.repeat === 'track'
                        ? 'bg-green-600 border-green-600 text-white'
                        : 'bg-slate-700 border-slate-600 text-white'
                    }`}
                  >
                    Repeat 1
                  </Button>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                onClick={() => {
                  setIsSpotifyDialogOpen(false);
                  setSpotifyOptions({ shuffle: false, repeat: 'off' });
                }}
                variant="outline"
                className="flex-1 bg-slate-700 border-slate-600 text-white"
              >
                Cancelar
              </Button>
              <Button
                onClick={sendToSpotify}
                disabled={selectedForSpotify.length === 0}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                <Music className="w-4 h-4 mr-2" />
                Enviar {selectedForSpotify.length} Ahora
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
